import React, { ReactElement } from 'react'
import { CheckCircle } from '@mui/icons-material'
import { Box, Chip, LinearProgress, Typography } from '@mui/material'
import { format } from 'date-fns'

import { CheckIcon, ProcessIcon } from '~assets/icons'
import { IdentityChip } from '~components/common/IdentityChip'
import { Identity2, Input1, ProcessSummary } from '~types'

interface ProcessDetailsProps {
  pinnedInputs: Input1[]
  processSummary: ProcessSummary | null
  identityMap: Record<string, Identity2>
}

export interface PinnedInputProps {
  input: Input1
  identityMap: Record<string, Identity2>
}

export const PinnedFieldChip: React.FC<PinnedInputProps> = ({ input, identityMap }) => {
  const identity = input.value?.identity_value?.identity
    ? identityMap[input.value.identity_value.identity]
    : null

  const renderChipContent = (): ReactElement | null => {
    switch (input.type) {
      case 'identity':
        return identity ? <IdentityChip identity={identity} /> : null
      case 'bool':
        return input.value?.bool_value && input.value?.bool_value.checked ? (
          <Chip
            icon={<CheckIcon width={14} />}
            sx={{
              padding: '0px 8px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              '& .MuiChip-label': {
                padding: 0,
              },
            }}
          />
        ) : null
      case 'datetime':
        const dateValue = input.value?.datetime_value?.datetime
        return dateValue ? <Chip label={format(new Date(dateValue), 'yyyy-MM-dd HH:mm')} /> : null
      case 'file':
        return <Chip icon={<CheckCircle color='success' />} label='File uploaded' />
      case 'location':
        const location = input.value?.location_value
        const address = `${location?.street}, ${location?.city}, ${location?.state} ${location?.zip}`
        return location ? <Chip label={address} /> : null
      case 'number':
        return input.value?.number_value ? (
          <Chip label={Number(input.value?.number_value.number)} />
        ) : null
      case 'text':
        return input.value?.text_value?.text ? (
          <Chip
            label={String(input.value?.text_value?.text)}
            sx={{
              maxWidth: '300px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          />
        ) : null
      default:
        return null
    }
  }

  const chipContent = renderChipContent()
  return (
    chipContent !== null && (
      <Box className='flex-v-align' gap={1}>
        <Typography variant='label' sx={{ textTransform: 'uppercase' }}>
          {input.name}
        </Typography>
        {chipContent}
      </Box>
    )
  )
}

export interface ProcessProgressProps {
  completedSteps: number
  totalSteps: number
  currentStepName: string
  showStepCount?: boolean
  big?: boolean
}

export const ProcessProgress: React.FC<ProcessProgressProps> = ({
  completedSteps,
  totalSteps,
  currentStepName,
  showStepCount = true,
  big = false,
}) => {
  return (
    <>
      {big && (
        <Typography variant='body1' color='text.primary' sx={{ mb: 1 }}>
          {completedSteps === totalSteps
            ? 'All Steps Completed'
            : `Current Step (${completedSteps + 1} of ${totalSteps}): ${currentStepName}`}
        </Typography>
      )}
      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 2 }}>
        <LinearProgress
          variant='determinate'
          value={(completedSteps / totalSteps) * 100}
          sx={{
            flexGrow: 1,
            height: big ? 8 : 4,
            borderRadius: big ? 4 : 0,
            '& .MuiLinearProgress-bar': {
              backgroundColor:
                completedSteps < totalSteps
                  ? 'primary.main' // blue
                  : 'success.main', // green,
              borderRadius: big ? 4 : 0,
            },
          }}
        />
        {showStepCount && (
          <Typography variant='body1'>
            {completedSteps}/{totalSteps}
          </Typography>
        )}
      </Box>
      {!big && (
        <Typography variant='body1' color='text.secondary'>
          {completedSteps === totalSteps
            ? 'All Steps Completed'
            : `Current Step (${completedSteps + 1} of ${totalSteps}): ${currentStepName}`}
        </Typography>
      )}
    </>
  )
}

const ProcessDetails: React.FC<ProcessDetailsProps> = ({
  pinnedInputs,
  processSummary,
  identityMap,
}) => {
  return (
    <Box
      display='flex'
      sx={{
        flexDirection: 'column',
        alignContent: 'flex-start',
      }}
      gap={1}
    >
      {processSummary && (
        <>
          <Box display='flex' gap={1}>
            <ProcessIcon width={16} />
            <p>{processSummary.name}</p>
          </Box>
          <ProcessProgress
            completedSteps={processSummary.completed_steps}
            totalSteps={processSummary.total_steps}
            currentStepName={processSummary.current_step_name || ''}
          />
        </>
      )}
      {pinnedInputs.map((piField) => (
        <PinnedFieldChip
          key={`pinned-field-${piField.name}-${piField.type}-${piField.id}`}
          input={piField}
          identityMap={identityMap}
        />
      ))}
    </Box>
  )
}

export default ProcessDetails
